import { ChangeDetectionStrategy, Component, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, filter, mapTo, switchMap, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NgChartsModule } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BazaNcRecurringTransactionsDataAccess } from '@scaliolabs/baza-nc-data-access';
import { BazaFormValidatorService } from '@scaliolabs/baza-core-ng';
import { BazaNcRecurringTransactionCompoundRevenueCalculationResponse, RecurringTransactionFrequency } from '@scaliolabs/baza-nc-shared';
import { BazaWebUtilSharedService, catchErrorMessage, Message, StorageService, UtilModule } from '@scaliolabs/baza-web-utils';

import { Calculator, CalculatorForm } from './models';

@UntilDestroy()
@Component({
    selector: 'app-calculator-form',
    templateUrl: './calculator-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        NgChartsModule,
        NzAlertModule,
        NzButtonModule,
        NzFormModule,
        NzInputModule,
        NzInputNumberModule,
        NzRadioModule,
        NzModalModule,
        NzSelectModule,
        NzSkeletonModule,
        ReactiveFormsModule,
        UtilModule,
    ],
})
export class CalculatorFormComponent implements AfterViewInit {
    @Input()
    minNumber: number;

    @Input()
    maxNumber: number;

    @Input()
    pricePerShare: number;

    @Output()
    calcChange = new EventEmitter<Calculator>();

    public amountItems = [1, 2, 4, 5, 10];
    public calculatorForm: CalculatorForm;
    public compoundRevenue: BazaNcRecurringTransactionCompoundRevenueCalculationResponse;
    public FREQUENCY = RecurringTransactionFrequency;
    public i18nFormCalcPath = 'uic.calculatorForm.calc';
    public i18nFormFieldsPath = 'uic.calculatorForm.fields';
    public message$ = new BehaviorSubject<Message | undefined>(undefined);

    public barChartData: ChartData<'bar'>;
    public barChartOptions: ChartConfiguration<'bar'>['options'] = {
        responsive: true,
        scales: {
            x: {
                grid: { display: false },
            },
            y: {
                display: false,
                grid: { display: false },
            },
        },
        plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
        },
    };

    shareMapping: { [k: string]: string } = {
        '=1': '# share',
        other: '# shares',
    };

    constructor(
        private readonly fb: FormBuilder,
        private readonly router: Router,
        private readonly storageService: StorageService,
        private readonly transactionsDataAccess: BazaNcRecurringTransactionsDataAccess,
        public readonly bazaFormValidatorService: BazaFormValidatorService,
        public readonly wts: BazaWebUtilSharedService,
    ) {
        this.onFormReset();
    }

    ngAfterViewInit(): void {
        this.calculatorForm.valueChanges
            .pipe(
                debounceTime(500),
                filter(() => this.calculatorForm.valid),
                tap(() => {
                    this.compoundRevenue = null;
                    this.message$.next(undefined);
                }),
                switchMap(() => {
                    const calc: Calculator = this.calculatorForm.value;

                    // send new values to parent component
                    this.calcChange.emit(calc);

                    // update local storage with new values
                    this.storageService.setObject('calc', calc);

                    // draw chart with new values
                    this.barChartData = {
                        labels: this.generatePortfolioData({
                            start: 0,
                            contribution:
                                calc.frequency === RecurringTransactionFrequency.Weekly
                                    ? calc.amount * this.pricePerShare * 4
                                    : calc.amount * this.pricePerShare,
                            annualReturn: calc.return / 100,
                            years: parseInt(calc.horizon),
                        }).labels,
                        datasets: [
                            {
                                data: this.generatePortfolioData({
                                    start: 0,
                                    contribution:
                                        calc.frequency === RecurringTransactionFrequency.Weekly
                                            ? calc.amount * this.pricePerShare * 4
                                            : calc.amount * this.pricePerShare,
                                    annualReturn: calc.return / 100,
                                    years: parseInt(calc.horizon),
                                }).portfolio,
                                backgroundColor: '#13BA74',
                                borderRadius: 4,
                                barThickness: 15,
                            },
                        ],
                    };

                    // re-calculate compound revenue with new values
                    const request = {
                        averageAnnualReturnPercentage: calc.return,
                        investmentFrequency: calc.frequency,
                        investmentValueCents: calc.amount * this.pricePerShare * 100,
                        numberOfYears: parseInt(calc.horizon),
                    };

                    return this.transactionsDataAccess.calculateCompoundRevenue(request).pipe(
                        tap((response) => {
                            this.compoundRevenue = response;
                        }),
                        mapTo(''),
                        catchErrorMessage,
                    );
                }),
                untilDestroyed(this),
            )
            .subscribe((message) => {
                this.message$.next(message as Message | undefined);
            });

        this.checkNumberOfShares();
        this.calculatorForm.updateValueAndValidity({ emitEvent: true });
    }

    // onFormReset
    public onFormReset(): void {
        const calc: Calculator = this.storageService.getObject('calc');

        this.calculatorForm = this.fb.group({
            frequency: [calc?.frequency || RecurringTransactionFrequency.Weekly],
            amount: [calc?.amount || 1],
            numberOfShares: [calc?.numberOfShares || 1],
            return: [calc?.return || 8, Validators.required],
            horizon: [calc?.horizon || '40'],
        }) as CalculatorForm;
    }

    // onAmountChange
    public onAmountChange(e) {
        if (e !== this.calculatorForm.controls.numberOfShares.value && e >= this.minNumber && e <= this.maxNumber) {
            this.calculatorForm.controls.numberOfShares.setValue(e);
        }
    }

    // onNumberOfSharesChange
    public onNumberOfSharesChange(e) {
        if (e !== this.calculatorForm.controls.amount.value && e >= this.minNumber && e <= this.maxNumber) {
            this.calculatorForm.controls.amount.setValue(e);
        }
    }

    // onModalClose
    public onModalClose() {
        this.router.navigate([{ outlets: { modal: null } }], {
            state: { skipScroll: true },
        });
    }

    // decreaseShares
    public decreaseShares(): void {
        const control = this.calculatorForm.controls.numberOfShares;

        if (control.value - 1 >= 1) {
            control.setValue(control.value - 1);
        }
    }

    // increaseShares
    public increaseShares(): void {
        const control = this.calculatorForm.controls.numberOfShares;

        if (control.value + 1 <= this.maxNumber) {
            control.setValue(control.value + 1);
        }
    }

    // checkNumberOfShares
    checkNumberOfShares(): void {
        const control = this.calculatorForm.controls.numberOfShares;

        if (control.value < this.minNumber) {
            control.setValue(this.minNumber);
        } else if (control.value > this.maxNumber) {
            control.setValue(this.maxNumber);
        }
    }

    generatePortfolioData({ start = 1000, contribution = 100, annualReturn = 0.08, years = 40 }) {
        const labels = [];
        const portfolio = [];

        let investment = start;

        for (let year = 1; year <= years; year++) {
            investment += contribution * 12;
            investment *= 1 + annualReturn;

            if (years > 20) {
                if (year % 2 === 0) {
                    labels.push('');
                    portfolio.push(Math.round(investment));
                }

                if (year % 10 === 0) {
                    labels[year / 2 - 1] = `Year ${year}`;
                }
            } else {
                labels.push('');
                portfolio.push(Math.round(investment));

                if (year % 5 === 0) {
                    labels[year - 1] = `Year ${year}`;
                }
            }
        }

        return { labels, portfolio };
    }

    formatterPercent = (value: number): string => `${value}%`;
}
